import React, {useState} from "react";
import {Button, ButtonVariant} from "@amzn/stencil-react-components/button";
import {IconStarFill} from '@amzn/stencil-react-components/icons';
import {Label, Text} from "@amzn/stencil-react-components/text";
import {getEventModel, promoteDerivedSegmentationAttributes, upsertEventModel} from "src/service/service";
import {APICallStatus, EventModelStatus, Stage} from "src/enum/enums";
import {ModalContent, WithModal} from '@amzn/stencil-react-components/modal';
import {Input} from "@amzn/stencil-react-components/form";
import {getStage, isMcmUrlValid, isUserEligibleForProdPromotion} from "src/util/commonUtil";
import "src/components/commonStyles.scss";
import {Spinner} from "@amzn/stencil-react-components/spinner";
import {
    AUTO_DISMISS_TIMEOUT,
    overridePromoteToProdForSolutionTypes,
    SolutionTypes
} from "src/constant/constants";
import {useAppDispatch, useAppSelector} from "src/state/hooks";
import {MessageBanner, MessageBannerType} from "@amzn/stencil-react-components/message-banner";
import {PROMOTE_TO_PROD_ERROR_MESSAGE} from "src/constant/errorConstants";
import {updatePromoteToProdError, updatePromoteToProdStatus} from "src/state/slice/EventModelSlice";

type props = {
  eventSolutionType:string,
  eventModelName: string,
  solutionType: string,
  isLiveEventModelPresentInBeta: boolean,
  isValidSolutionDetailsInput: boolean,
  isValidRule:boolean,
}

const STRINGS_DISPLAYED = {
  promote_to_prod_modal_title: "Promote event model to prod",
  launch_button: "Launch",
  done_button: "Done",
  enter_mcm_url: "Please enter the MCM URL",
  promote_to_prod_button: "Promote to Prod!",
  promote_to_prod_successful: "Promote to Prod is sucessful!"
}

/**
 * Renders the promote to prod button with a modal for validations.
 */
export const PromoteToProdButtonWithModal = ({eventSolutionType, eventModelName, solutionType, isLiveEventModelPresentInBeta, isValidSolutionDetailsInput, isValidRule}: props) => {
  const dispatch = useAppDispatch();
  const [mcmUrl, setMcmUrl] = useState<string>();
  const [promoteToProdStatus, setPromoteToProdStatus] = useState<APICallStatus>(APICallStatus.NOT_STARTED);
  const contactAlertFileMetadataState = useAppSelector(({contactAlertFileMetadataComponent}) => contactAlertFileMetadataComponent);
  const [promoteToProdErrorMessage, setPromoteToProdErrorMessage] = useState("");

  const isPromoteToProdEnabled = () => {
    return !(isLiveEventModelPresentInBeta && isValidSolutionDetailsInput && isValidRule && (isUserEligibleForProdPromotion() || overridePromoteToProdForSolutionTypes.includes(solutionType)));
  }

  const onClickPromoteToProd = async () => {
    try{
      setPromoteToProdStatus(APICallStatus.IN_PROGRESS);

      // fetch live event model from beta
      const eventModelInBeta = await getEventModel(eventModelName, EventModelStatus.LIVE, Stage.beta);

        // promote derived segmentation attributes for contact alerts in prod
        if (eventModelInBeta?.eventSolution?.type === SolutionTypes.CONTACT_ALERT &&
            getStage() as Stage === Stage.prod &&
            contactAlertFileMetadataState?.newBulkUploadFileAttributes.length > 0) {
            await promoteDerivedSegmentationAttributes(contactAlertFileMetadataState.newBulkUploadFileAttributes, Stage.prod);
        }

      // push the beta event model to prod
      await upsertEventModel(eventModelInBeta, EventModelStatus.LIVE, Stage.prod);

      setPromoteToProdStatus(APICallStatus.SUCCESSFUL);
      if (eventSolutionType === SolutionTypes.CONTACT_ALERT) {
          dispatch(updatePromoteToProdStatus(APICallStatus.SUCCESSFUL.toString()))
      }
    } catch(error) {
      setPromoteToProdStatus(APICallStatus.FAILED);
      setPromoteToProdErrorMessage(error?.response?.data?.message);
      if(eventSolutionType === SolutionTypes.CONTACT_ALERT) {
          dispatch(updatePromoteToProdStatus(APICallStatus.FAILED.toString()))
          dispatch(updatePromoteToProdError(error?.response?.data?.message));
      }
    }
  };

    const showMessageBanner = () => {
        if(promoteToProdStatus === APICallStatus.SUCCESSFUL) {
            return (<MessageBanner
                dismissButtonAltText="Dismiss"
                isDismissible
                autoDismissAfter={AUTO_DISMISS_TIMEOUT}
                onDismissed={() => {
                    setPromoteToProdStatus(APICallStatus.COMPLETED);
                }}
                type={MessageBannerType.Success}
            >

                {STRINGS_DISPLAYED.promote_to_prod_successful}
            </MessageBanner>)
        }
        else if (promoteToProdStatus === APICallStatus.FAILED) {
            return (<MessageBanner
                dismissButtonAltText="Dismiss"
                isDismissible
                autoDismissAfter={AUTO_DISMISS_TIMEOUT}
                onDismissed={() => {
                    setPromoteToProdStatus(APICallStatus.COMPLETED);
                }}
                type={MessageBannerType.Error}
            >
                {PROMOTE_TO_PROD_ERROR_MESSAGE}<br/>{promoteToProdErrorMessage}
            </MessageBanner>)
        }
    }

  const renderPromoteToProdModal = ({ close }) => (
    <ModalContent
      titleText={STRINGS_DISPLAYED.promote_to_prod_modal_title}
      buttons={[
        <Button
          variant={ButtonVariant.Primary}
          icon={<IconStarFill aria-hidden={true}/>}
          className={"width_fit_content"}
          onClick={onClickPromoteToProd}
          disabled={!isMcmUrlValid(mcmUrl)}
        >
          <Text fontSize={"16px"}>{STRINGS_DISPLAYED.launch_button}</Text>
          {(promoteToProdStatus === APICallStatus.IN_PROGRESS) && <Spinner />}
        </Button>,
        <Button onClick={close} variant={ButtonVariant.Secondary}>
          {STRINGS_DISPLAYED.done_button}
        </Button>
      ]}>

      <Label htmlFor={"mcm-url-input"}>{STRINGS_DISPLAYED.enter_mcm_url}</Label>
      <Input 
        id={"mcm-url-input"} 
        placeholder={"https://mcm.amazon.com/cms/MCM-1234..."}
        width={"400px"}
        onChange={(e) => {
          setMcmUrl(e?.target?.value);
        }}
        value={mcmUrl}
      />
        {showMessageBanner()}
    </ModalContent>
  );

  return eventSolutionType == SolutionTypes.CONTACT_ALERT ?
      <Button
          variant={ButtonVariant.Secondary}
          icon={<IconStarFill aria-hidden={true}/>}
          className={"width_fit_content"}
          onClick={onClickPromoteToProd}
          disabled={isPromoteToProdEnabled()}
      >
          {STRINGS_DISPLAYED.promote_to_prod_button}
          {(promoteToProdStatus === APICallStatus.IN_PROGRESS) && <Spinner />}
      </Button>
      :
      <WithModal renderModal={renderPromoteToProdModal}>
          {({ open }) => (
              <Button
                  variant={ButtonVariant.Secondary}
                  icon={<IconStarFill aria-hidden={true}/>}
                  className={"width_fit_content"}
                  onClick={open}
                  disabled={isPromoteToProdEnabled()}
              >
                  {STRINGS_DISPLAYED.promote_to_prod_button}
              </Button>
          )}
      </WithModal>
}
